import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Form, Input, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { fetchCaptcha } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { MERCHANT_BRAND_NAME, MERCHANT_LOGO_URL } from "@/constants/brand";

type LoginForm = {
  account: string;
  password: string;
  captchaCode: string;
};

type FieldKey = "account" | "password" | "captchaCode";

function mapLoginError(raw: string): { field?: FieldKey; message: string } {
  const text = raw.trim();
  if (text.includes("验证码")) {
    return { field: "captchaCode", message: "验证码错误" };
  }
  if (text.includes("账号或密码") || text.includes("密码")) {
    return { field: "password", message: "密码错误" };
  }
  if (text.includes("账号")) {
    return { field: "account", message: text };
  }
  return { message: text || "登录失败" };
}

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm<LoginForm>();
  const [captchaId, setCaptchaId] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadCaptcha = useCallback(async () => {
    setLoadingCaptcha(true);
    try {
      const data = await fetchCaptcha();
      setCaptchaId(data.captchaId);
      setCaptchaImage(data.imageBase64);
      form.setFieldValue("captchaCode", "");
      form.setFields([{ name: "captchaCode", errors: [] }]);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "验证码加载失败");
    } finally {
      setLoadingCaptcha(false);
    }
  }, [form]);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onFinish = async (values: LoginForm) => {
    if (!captchaId) {
      form.setFields([{ name: "captchaCode", errors: ["请先获取验证码"] }]);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    form.setFields([
      { name: "account", errors: [] },
      { name: "password", errors: [] },
      { name: "captchaCode", errors: [] },
    ]);

    try {
      await login({
        account: values.account.trim(),
        password: values.password,
        captchaId,
        captchaCode: values.captchaCode.trim(),
      });
      message.success("登录成功");
      navigate("/", { replace: true });
    } catch (err) {
      const mapped = mapLoginError(err instanceof Error ? err.message : "登录失败");
      if (mapped.field) {
        form.setFields([{ name: mapped.field, errors: [mapped.message] }]);
      } else {
        setFormError(mapped.message);
      }
      void loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-shell">
      <header className="login-topbar">
        <img src={MERCHANT_LOGO_URL} alt={MERCHANT_BRAND_NAME} className="login-topbar__logo" />
      </header>

      <div className="login-layout">
        <section className="login-brand">
          <h1 className="login-brand__title">欢迎登录</h1>
          <p className="login-brand__lead">商户交易流水中心，查看收款与对账明细</p>
        </section>

        <section className="login-panel">
          <div className="login-card">
            <div className="login-card__header">
              <h2>登录你的账户</h2>
              <p>请输入账号信息继续</p>
            </div>

            {formError ? <div className="login-form-error">{formError}</div> : null}

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              className="login-form"
            >
              <Form.Item
                name="account"
                rules={[{ required: true, message: "请输入账号" }]}
              >
                <Input placeholder="请输入账号" autoComplete="username" size="large" />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: "请输入密码" }]}
              >
                <Input.Password placeholder="请输入密码" autoComplete="current-password" size="large" />
              </Form.Item>

              <div className="login-captcha-row">
                <Form.Item
                  name="captchaCode"
                  rules={[{ required: true, message: "请输入验证码" }]}
                  className="login-captcha-field"
                >
                  <Input placeholder="请输入验证码" maxLength={8} size="large" />
                </Form.Item>
                <button
                  type="button"
                  className="login-captcha__image"
                  onClick={() => void loadCaptcha()}
                  title="点击刷新验证码"
                  disabled={loadingCaptcha}
                >
                  {captchaImage ? (
                    <img
                      src={`data:image/svg+xml;base64,${captchaImage}`}
                      alt="验证码"
                      width={120}
                      height={40}
                    />
                  ) : (
                    <span className="login-captcha__placeholder">
                      <ReloadOutlined spin={loadingCaptcha} />
                    </span>
                  )}
                </button>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={submitting}
                className="login-submit"
              >
                登录
              </Button>
            </Form>
          </div>
        </section>
      </div>
    </div>
  );
}
